import React, { useState, useEffect, useMemo } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  LinearProgress,
  IconButton,
  Tooltip,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@material-ui/core';
import {
  Security,
  People,
  Warning,
  CheckCircle,
  Refresh,
  TrendingUp,
  TrendingDown,
  Timeline,
  Business,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import usePortalAuth from '../../../auth/usePortalAuth';

// Import existing components we'll enhance
import useChangePageTitle from '../CustomHooks/useChangePageTitle';
import DialMetric from './DialMetric';
import LinkSafetyAnalytics from './LinkSafetyAnalytics';
import LinkVectorAnalysis from './LinkVectorAnalysis';
import UserClickActivityChart from './UserClickActivityChart';
import RealityBasedPhishingSafety from './RealityBasedPhishingSafety';
import TrainingAnalytics from './TrainingAnalytics';
import { propTypes } from 'react-world-flags';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    background: theme.palette.type === 'dark' ? theme.palette.background.default : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    minHeight: '100vh',
  },
  controlBar: {
    background: theme.palette.type === 'dark' ? theme.palette.background.paper : 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    borderRadius: 12,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    border: '1px solid rgba(255,255,255,0.2)',
  },
  heroCard: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    borderRadius: 16,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: 0,
      width: '200px',
      height: '200px',
      background: 'rgba(255,255,255,0.1)',
      borderRadius: '50%',
      transform: 'translate(50%, -50%)',
    },
  },
  kpiCard: {
    borderRadius: 12,
    padding: theme.spacing(2.5),
    height: '100%',
    background: theme.palette.type === 'dark' ? theme.palette.background.paper : 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255,255,255,0.2)',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
    },
  },
  kpiIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
  },
  trendChip: {
    fontWeight: 600,
    fontSize: '0.75rem',
  },
  scoreDisplay: {
    textAlign: 'center',
    padding: theme.spacing(2),
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    marginTop: theme.spacing(1),
  },
  dataSection: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    color: theme.palette.type === 'dark' ? theme.palette.text.primary : '#1a237e',
  },
  valueBox: {
    textAlign: 'center',
    padding: theme.spacing(2),
    background: theme.palette.type === 'dark' ? theme.palette.background.paper : 'rgba(255, 255, 255, 0.8)',
    borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.3)',
  },
  activitySummaryBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor: theme.palette.type === 'dark' ? '#2a2a2a' : '#f5f5f5',
  },
  modernButton: {
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 600,
    padding: theme.spacing(1, 3),
  },
}));

const CompanyAdminOverview = ({ 
  companyBoxStats, // Direct prop from Dashboard.js
  companyAdminData, // For other company admin data
  companyAlertStats, // Alert stats (today/sevenDays/thirtyDays buckets)
  trainingAdminData, // Training admin data
  trainingData, // User training data with module titles
  threatModel, // Direct prop from Dashboard.js
  clickStats, // Click activity data for charts
  userOverviewData, // Reality-based phishing safety data
  viewMode, // View mode from parent
  onViewModeChange, // View mode change handler from parent
  isLoading, 
  onRefresh,
  getTranslation,
  preferredLanguage
}) => {
  useChangePageTitle('Company Overview - Admin Dashboard');
  const classes = useStyles();
  const { getAccessTokenSilently } = usePortalAuth();

  //const getTranslation = props.getTranslation
  //const preferredLanguage = props.preferredLanguage

  // State management
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [dateRange, setDateRange] = useState('30days'); // Default to 30 days

  // Extract and process company admin data
  const processedData = useMemo(() => {
    // Use companyBoxStats directly from props (from Dashboard.js)
    const boxStats = companyBoxStats || {};
    
    if (!companyAdminData || typeof companyAdminData !== 'object') {
      return {
        getCompanyBoxStats: boxStats,
        alerts: {},
        users: {},
        links: {},
        safety: {},
        stats: {}
      };
    }

    // Extract the key data points for analytics from your actual structure
    return {
      getCompanyBoxStats: boxStats, // Use the direct prop
      alerts: companyAdminData.alerts || {},
      users: companyAdminData.users || {},
      links: companyAdminData.links || {},
      safety: companyAdminData.safety || {},
      stats: companyAdminData.stats || {},
      // Add any other sections that exist in your company_admin data
      raw: companyAdminData
    };
  }, [companyBoxStats, companyAdminData]);

  // Calculate key metrics from the company admin data
  const metrics = useMemo(() => {
    const boxStats = processedData.getCompanyBoxStats;
    const data = processedData.raw;
    
    // Extract user metrics from getCompanyBoxStats
    const activeUsers = boxStats?.activeUsers || 0;
    const userCount = boxStats?.userCount || 0;
    const companyRiskScore = boxStats?.companyRiskScore || 0;
    
    // Calculate total alerts for the selected date range by summing the
    // per-day numeric values in the matching companyAlertStats bucket.
    // Each row is [timestamp, ...counts].
    const alertPeriodKey = dateRange === 'today'
      ? 'today'
      : dateRange === '7days'
        ? 'sevenDays'
        : 'thirtyDays';
    const alertRows = Array.isArray(companyAlertStats?.[alertPeriodKey])
      ? companyAlertStats[alertPeriodKey]
      : [];
    const totalAlerts = alertRows.reduce((sum, row) => {
      if (!Array.isArray(row)) return sum;
      // Skip index 0 (timestamp); sum remaining numeric columns.
      for (let i = 1; i < row.length; i++) {
        const v = Number(row[i]);
        if (Number.isFinite(v)) sum += v;
      }
      return sum;
    }, 0);

    const totalLinks = data?.totalLinks || data?.linkCount || 0;
    const safetyScore = data?.safetyScore || 0;
    
    // Calculate user activity percentage
    const userActivityPercentage = userCount > 0 ? (activeUsers / userCount) * 100 : 0;
    
    // Calculate risk exposure from link safety data
    const unsafeLinks = boxStats?.unsafeLinks;
    const scannedLinks = boxStats?.scannedLinks;
    const unknownLinks = boxStats?.unknownLinks;
    
    let riskExposure = 0;
    if (unsafeLinks && scannedLinks) {
      const totalUnsafeClicks = (unsafeLinks.clicked || 0) + (unknownLinks?.clicked || 0);
      const totalClicks = scannedLinks.clicked || 1; // Avoid division by zero
      riskExposure = Math.round((totalUnsafeClicks / totalClicks) * 100);
    }
    
    // Calculate training compliance metrics if training data exists
    let trainingCompliance = 0;
    let trainingStatusKey = 'No Data';
    
    if (trainingAdminData?.getUserLevelTrainingStats) {
      // Extract training data
      const userData = Object.values(trainingAdminData.getUserLevelTrainingStats).flat();
      
      if (userData.length > 0) {
        // Calculate completion rates
        let totalAssigned = 0;
        let totalCompleted = 0;
        
        userData.forEach(user => {
          const assigned = user.trainingCoursesAssigned?.length || 0;
          const completed = user.trainingCoursesCompleted?.length || 0;
          const inProgress = user.trainingCoursesInProgress?.length || 0;
          totalAssigned += assigned + completed + inProgress;
          totalCompleted += completed;
        });
        
        trainingCompliance = totalAssigned > 0 ? Math.round((totalCompleted / totalAssigned) * 100) : 0;
        
        // Determine status
        if (trainingCompliance >= 90) trainingStatusKey = 'Excellent';
        else if (trainingCompliance >= 70) trainingStatusKey = 'Good';
        else if (trainingCompliance >= 50) trainingStatusKey = 'Needs Attention';
        else trainingStatusKey = 'Critical';
      }
    }

    return {
      activeUsers,
      userCount,
      userActivityPercentage,
      companyRiskScore,
      totalAlerts,
      totalLinks,
      safetyScore,
      riskExposure,
      // Use companyRiskScore for security effectiveness
      securityEffectiveness: Math.round(companyRiskScore) || 75,
      threatResponseScore: 85, // Calculate based on actual data
      userRiskScore: Math.round(userActivityPercentage), // Based on user activity
      // Training compliance metrics
      trainingCompliance,
      trainingStatusKey,
    };
  }, [processedData, trainingAdminData, companyAlertStats, dateRange]);

  const handleRefresh = () => {
    setLastUpdated(new Date());
    if (onRefresh) {
      onRefresh();
    }
  };

  const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const getTrendIcon = (trend = 'stable') => {
    switch (trend) {
      case 'up': return <TrendingUp style={{ color: '#4caf50' }} />;
      case 'down': return <TrendingDown style={{ color: '#f44336' }} />;
      default: return <Timeline style={{ color: '#757575' }} />;
    }
  };

  const getScoreColor = (score) => {
    if (score >= 70) return '#4caf50'; // Green
    if (score >= 40) return '#ff9800'; // Yellow/Orange
    return '#f44336'; // Red
  };

  const getScoreBackgroundGradient = (score) => {
    if (score >= 70) {
      return 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)'; // Muted green gradient
    }
    if (score >= 40) {
      return 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)'; // Muted yellow/orange gradient
    }
    return 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)'; // Muted red gradient
  };

  const getScoreLabel = (score) => {
    if (score >= 70) return getTranslation('Excellent', preferredLanguage);
    if (score >= 40) return getTranslation('Needs Attention', preferredLanguage);
    return getTranslation('Critical', preferredLanguage);
  };

  return (
    <Box className={classes.root}>
      {/* Centered Header with Better Spacing */}
      <Paper className={classes.controlBar}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap">
          <Typography 
            variant="h4" 
            className={classes.sectionTitle}
            style={{ 
              fontWeight: 700, 
            }}
          >
            {getTranslation('Company Overview', preferredLanguage)}
          </Typography>
          
          <Box display="flex" alignItems="center" style={{ gap: 16 }}>
            <FormControl size="small" variant="outlined" style={{ minWidth: 120 }}>
              <InputLabel id="date-range-label">{getTranslation('Date Range', preferredLanguage)}</InputLabel>
              <Select
                labelId="date-range-label"
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                label={getTranslation('Date Range', preferredLanguage)}
              >
                <MenuItem value="today">{getTranslation('Today', preferredLanguage)}</MenuItem>
                <MenuItem value="7days">{getTranslation('7 Days', preferredLanguage)}</MenuItem>
                <MenuItem value="30days">{getTranslation('30 Days', preferredLanguage)}</MenuItem>
              </Select>
            </FormControl>
            
          

            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <Tooltip title={getTranslation('Refresh Data', preferredLanguage)}>
                <IconButton 
                  onClick={handleRefresh} 
                  color="primary" 
                  size="small"
                  style={{ backgroundColor: 'rgba(25, 118, 210, 0.08)' }}
                >
                  <Refresh fontSize="small" />
                </IconButton>
              </Tooltip>
              <Typography variant="caption" color="textSecondary">
                {lastUpdated.toLocaleTimeString()}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* KPI Cards */}
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        {/* Threat Response Score */}
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.kpiCard}>
            <CardContent>
              <Box
                className={classes.kpiIcon}
                style={{ backgroundColor: '#e3f2fd', color: '#1976d2' }}
              >
                <Security />
              </Box>
              
              <Typography variant="h4" style={{ fontWeight: 700, color: getScoreColor(metrics.securityEffectiveness) }}>
                {metrics.securityEffectiveness}%
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {getTranslation('Company Risk Score', preferredLanguage)}
              </Typography>
              
              <Chip
                size="small"
                icon={getTrendIcon(metrics.securityEffectiveness >= 70 ? 'up' : metrics.securityEffectiveness >= 40 ? 'stable' : 'down')}
                label={getScoreLabel(metrics.securityEffectiveness)}
                className={classes.trendChip}
                style={{ 
                  backgroundColor: metrics.securityEffectiveness >= 70 ? '#e8f5e8' : metrics.securityEffectiveness >= 40 ? '#fff3e0' : '#ffebee', 
                  color: getScoreColor(metrics.securityEffectiveness)
                }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* User Security Score */}
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.kpiCard}>
            <CardContent>
              <Box
                className={classes.kpiIcon}
                style={{ backgroundColor: '#f3e5f5', color: '#7b1fa2' }}
              >
                <People />
              </Box>
              
              <Typography variant="h4" style={{ fontWeight: 700, color: '#7b1fa2' }}>
                {Math.round(metrics.userActivityPercentage)}%
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {getTranslation('User Activity Rate', preferredLanguage)}
              </Typography>
              
              <Chip
                size="small"
                icon={getTrendIcon(metrics.userActivityPercentage >= 80 ? 'up' : 'stable')}
                label={
                  metrics.userActivityPercentage >= 80
                    ? getTranslation('Excellent user engagement', preferredLanguage)
                    : metrics.userActivityPercentage >= 60
                      ? getTranslation('Good user activity', preferredLanguage)
                      : getTranslation('User activity needs attention', preferredLanguage)
                }
                className={classes.trendChip}
                style={{ backgroundColor: '#f3e5f5', color: '#7b1fa2' }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Active Alerts */}
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.kpiCard}>
            <CardContent>
              <Box
                className={classes.kpiIcon}
                style={{ backgroundColor: '#fce4ec', color: '#c2185b' }}
              >
                <Warning />
              </Box>
              
              <Typography variant="h4" style={{ fontWeight: 700, color: '#c2185b' }}>
                {formatNumber(metrics.totalAlerts)}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {getTranslation('Active Alerts', preferredLanguage)}
              </Typography>
              
              <Chip
                size="small"
                icon={getTrendIcon(metrics.totalAlerts === 0 ? 'stable' : 'up')}
                label={
                  dateRange === 'today'
                    ? getTranslation('Today', preferredLanguage)
                    : dateRange === '7days'
                      ? getTranslation('Last 7 Days', preferredLanguage)
                      : getTranslation('Last 30 Days', preferredLanguage)
                }
                className={classes.trendChip}
                style={{
                  backgroundColor: metrics.totalAlerts === 0 ? '#e8f5e8' : '#fce4ec',
                  color: metrics.totalAlerts === 0 ? '#2e7d32' : '#c2185b'
                }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Training Compliance */}
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.kpiCard}>
            <CardContent>
              <Box
                className={classes.kpiIcon}
                style={{ 
                  backgroundColor: metrics.trainingCompliance >= 90 ? '#e8f5e8' : metrics.trainingCompliance >= 70 ? '#fff3e0' : metrics.trainingCompliance >= 50 ? '#fff3e0' : '#ffebee', 
                  color: metrics.trainingCompliance >= 90 ? '#388e3c' : metrics.trainingCompliance >= 70 ? '#f57c00' : metrics.trainingCompliance >= 50 ? '#f57c00' : '#d32f2f'
                }}
              >
                <CheckCircle />
              </Box>
              
              <Typography 
                variant="h4" 
                style={{ 
                  fontWeight: 700, 
                  color: metrics.trainingCompliance >= 90 ? '#388e3c' : metrics.trainingCompliance >= 70 ? '#f57c00' : metrics.trainingCompliance >= 50 ? '#f57c00' : '#d32f2f'
                }}
              >
                {metrics.trainingCompliance}%
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {getTranslation('Training Compliance', preferredLanguage)}
              </Typography>
              
              <Chip
                size="small"
                icon={getTrendIcon(metrics.trainingCompliance >= 90 ? 'up' : metrics.trainingCompliance >= 70 ? 'stable' : 'down')}
                label={getTranslation(metrics.trainingStatusKey, preferredLanguage)}
                className={classes.trendChip}
                style={{ 
                  backgroundColor: metrics.trainingCompliance >= 90 ? '#e8f5e8' : metrics.trainingCompliance >= 70 ? '#fff3e0' : metrics.trainingCompliance >= 50 ? '#fff3e0' : '#ffebee',
                  color: metrics.trainingCompliance >= 90 ? '#2e7d32' : metrics.trainingCompliance >= 70 ? '#f57c00' : metrics.trainingCompliance >= 50 ? '#f57c00' : '#d32f2f'
                }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>


      {/* User Activity Overview with Chart */}
      <Typography variant="h5" className={classes.sectionTitle}>
        {getTranslation('User Activity Overview', preferredLanguage)}
      </Typography>
      
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        {/* User Activity Metrics */}
        <Grid item xs={12} md={5}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <DialMetric
                activeUsers={metrics.activeUsers}
                userCount={metrics.userCount}
                title={getTranslation('User Activity Status', preferredLanguage)}
                getTranslation={getTranslation}
                preferredLanguage={preferredLanguage}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Paper style={{ padding: 16, borderRadius: 12 }}>
                <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 16 }}>
                  {getTranslation('User Activity Insights', preferredLanguage)}
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Box className={classes.valueBox}>
                      <Typography variant="h4" style={{ color: '#4caf50', fontWeight: 700 }}>
                        {formatNumber(metrics.activeUsers)}
                      </Typography>
                      <Typography variant="caption">{getTranslation('Active Users', preferredLanguage)}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box className={classes.valueBox}>
                      <Typography variant="h4" style={{ color: '#1976d2', fontWeight: 700 }}>
                        {formatNumber(metrics.userCount)}
                      </Typography>
                      <Typography variant="caption">{getTranslation('Total Users', preferredLanguage)}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box className={classes.valueBox}>
                      <Typography variant="h4" style={{ color: '#f44336', fontWeight: 700 }}>
                        {formatNumber(metrics.userCount - metrics.activeUsers)}
                      </Typography>
                      <Typography variant="caption">{getTranslation('Inactive Users', preferredLanguage)}</Typography>
                    </Box>
                  </Grid>
                </Grid>
                
                <Box className={classes.activitySummaryBox}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>{getTranslation('Activity Rate', preferredLanguage)}:</strong> {Math.round(metrics.userActivityPercentage)}% {getTranslation('of users are currently active', preferredLanguage)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
                    <strong>{getTranslation('Status', preferredLanguage)}:</strong> {metrics.userActivityPercentage >= 80 ? 
                      getTranslation('Excellent user engagement', preferredLanguage) : 
                      metrics.userActivityPercentage >= 60 ? 
                      getTranslation('Good user activity', preferredLanguage) : 
                      getTranslation('User activity needs attention', preferredLanguage)
                    }
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
        
        {/* User Click Activity Chart */}
        <Grid item xs={12} md={7}>
          <UserClickActivityChart 
            clickStatsData={clickStats} 
            dateRange={dateRange}
            getTranslation={getTranslation}
            preferredLanguage={preferredLanguage}
          />
        </Grid>
      </Grid>

      {/* Reality Based Phishing Safety Section */}
      <RealityBasedPhishingSafety 
        userOverviewData={userOverviewData} 
        dateRange={dateRange}
        getTranslation={getTranslation}
        preferredLanguage={preferredLanguage}
      />

      {/* Training Analytics Section - Uses own date controls */}
      <TrainingAnalytics 
        trainingAdminData={trainingAdminData} 
        trainingData={trainingData}
        getTranslation={getTranslation}
        preferredLanguage={preferredLanguage}
      />

      {/* Link Analytics Section */}
      <Typography variant="h5" className={classes.sectionTitle}>
        {getTranslation('Security Analytics', preferredLanguage)}
      </Typography>
      
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        {/* Link Safety Analytics - Half Screen */}
        {companyBoxStats?.scannedLinks && (
          <Grid item xs={12} md={6}>
            <LinkSafetyAnalytics
              scannedLinks={companyBoxStats.scannedLinks}
              unknownLinks={companyBoxStats.unknownLinks}
              unsafeLinks={companyBoxStats.unsafeLinks}
              dateRange={dateRange}
              getTranslation={getTranslation}
              preferredLanguage={preferredLanguage}
            />
          </Grid>
        )}
        
        {/* Link Vector Analysis - Half Screen */}
        <Grid item xs={12} md={6}>
          <LinkVectorAnalysis
            getThreatModelData={threatModel}
            dateRange={dateRange}
            getTranslation={getTranslation}
            preferredLanguage={preferredLanguage}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default CompanyAdminOverview;